import { useCallback, useEffect, useRef, useState } from "react";
import { api, UnauthorizedError } from "@/lib/api";
import { t } from "@/lib/i18n";
import type { IngestHealth, LogEntry, LogResponse, LogStatsResponse } from "@/lib/types";

const REFRESH_JITTER_PCT = 0.2;

export type OverviewData = {
  /** Treffer-Count "Logs heute" (filter-aware vom Backend). */
  total24h: number;
  errors24h: number;
  warnings24h: number;
  sources: number;
  recentErrors: LogEntry[];
  ingest: IngestHealth | null;
  /** Zeitpunkt des letzten erfolgreichen (auch partiellen) Refreshs. */
  lastUpdated: string | null;
};

type State = {
  data: OverviewData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

export function useOverviewData(enabled: boolean): State {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const nextDelayMs = () => {
    const jitter = (Math.random() * 2 - 1) * REFRESH_JITTER_PCT;
    return Math.max(5_000, Math.round(20_000 * (1 + jitter)));
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    // 24h-Fenster für "heute"-Counts. Die Stats laufen über `window_sec`
    // (rollierendes Fenster, serverseitig aufgelöst): der Cache-Key bleibt
    // damit über Polls hinweg stabil und der Stale-While-Revalidate-Cache
    // des Backends liefert die Zahlen sofort — neu gezählt wird asynchron.
    // Die Recent-Errors-Liste nutzt weiter ein absolutes `start_time`.
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const startParam = `start_time=${encodeURIComponent(since)}`;
    try {
      const [statsResult, errorsResult, clientsResult, ingestResult] = await Promise.allSettled([
        api.get<LogStatsResponse>("/api/logs/stats?window_sec=86400"),
        api.get<LogResponse>(`/api/logs?level=ERROR&limit=5&include_total=false&${startParam}`),
        api.get<{ clients: string[] }>("/api/clients"),
        api.get<IngestHealth>("/api/health/ingest"),
      ]);

      const hasUnauthorized = [statsResult, errorsResult, clientsResult, ingestResult].some(
        (r) => r.status === "rejected" && r.reason instanceof UnauthorizedError,
      );
      if (hasUnauthorized) return;

      const stats = statsResult.status === "fulfilled" ? statsResult.value : null;
      const errors = errorsResult.status === "fulfilled" ? errorsResult.value : null;
      const clientsResp = clientsResult.status === "fulfilled" ? clientsResult.value : null;
      const ingest = ingestResult.status === "fulfilled" ? ingestResult.value : null;

      const anySuccess = !!(stats || errors || clientsResp || ingest);

      setData((prev) => ({
        total24h: stats?.total ?? prev?.total24h ?? 0,
        errors24h: stats?.errors ?? prev?.errors24h ?? 0,
        warnings24h: stats?.warnings ?? prev?.warnings24h ?? 0,
        sources: clientsResp?.clients.length ?? prev?.sources ?? 0,
        recentErrors: errors?.logs.slice(0, 5) ?? prev?.recentErrors ?? [],
        ingest: ingest ?? prev?.ingest ?? null,
        lastUpdated: anySuccess ? new Date().toISOString() : (prev?.lastUpdated ?? null),
      }));

      if (!anySuccess) {
        setError(t("Daten aktuell nicht verfügbar"));
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const tick = async () => {
      await reload();
      if (!cancelled) {
        timerRef.current = window.setTimeout(() => void tick(), nextDelayMs());
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [enabled, reload]);

  return { data, loading, error, refresh: () => void reload() };
}
